<template>
  <div
    v-if="!loading"
    class="dashboard-page"
  >
    <div class="dashboard-page__title">
      <h1 class="ellipsis">{{ dashboardConfig?.title }}</h1>
    </div>
    <div class="dashboard-page__list">
      <div
        v-for="(group, groupId) in enrichedData"
        :key="groupId"
      >
        <div class="dashboard-page__group-header">
          <div
            v-if="group?.params"
            class="dashboard-page__dashboard-filters"
          >
            <div
              v-for="(param, paramId) in group.params"
              :key="`group-param-${paramId}`"
              class="dashboard-page__dashboard-filter"
            >
              <p class="dashboard-page__dashboard-filter-label">{{ param.title }}:</p>
              <component
                :is="getFilterComponent(param)"
                v-model="filters[groupId]['params'][param.label]"
                :style="{
                  width: `${param?.width ?? '100'}px`,
                }"
                class="dashboard-page__dashboard-filter-input"
                :placeholder="param?.placeholder ?? param.title"
                :config="param.type.includes('ref') ? param : param?.config"
                v-on="
                  param.type === 'date' || param.type.includes('ref')
                    ? { input: () => updateGroupDashboards(groupId) }
                    : {}
                "
                @enter="() => updateGroupDashboards(groupId)"
              />
            </div>
          </div>
          <h3 class="dashboard-page__group-title">{{ group?.title }}</h3>
        </div>
        <div
          :style="groupStyles(group)"
          class="dashboard-page__group-body"
        >
          <!-- Hardcode for statistics start -->
          <div
            v-for="(statisticGroup, statisticGroupId) in statisticsGroups"
            :key="`statistic-${statisticGroupId}`"
            :style="{
              display: 'grid',
              gap: '40px',
              gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
            }"
          >
            <div
              v-for="(dashboard, dashboardId) in statisticGroup?.dashboards"
              :key="dashboardId"
              :style="{ ...graphStyles(dashboard), maxWidth: '400px' }"
            >
              <component
                :is="graphComponent(dashboard?.type ?? 'bar')"
                v-if="dashboard?.data"
                :options="dashboard?.options"
                :data="dashboard?.data"
              />
            </div>
          </div>
          <!-- Hardcode for statistics end -->
          <div
            v-for="(dashboard, dashboardId) in group?.dashboards"
            :key="dashboardId"
            :style="graphStyles(dashboard)"
          >
            <div
              v-if="dashboard?.params"
              class="dashboard-page__dashboard-filters"
            >
              <div
                v-for="(param, paramId) in dashboard.params"
                :key="`dashboard-param-${paramId}`"
                class="dashboard-page__dashboard-filter"
              >
                <template v-if="!param?.froumGroup">
                  <p class="dashboard-page__dashboard-filter-label">{{ param.title }}:</p>
                  <component
                    :is="getFilterComponent(param)"
                    v-model="filters[groupId]['children'][dashboardId][param.label]"
                    class="dashboard-page__dashboard-filter-input"
                    :placeholder="param?.placeholder ?? param.title"
                    :config="param.type.includes('ref') ? param : param?.config"
                    v-on="
                      param.type === 'date' || param.type.includes('ref')
                        ? { input: () => updateDashboard(groupId, dashboardId) }
                        : {}
                    "
                    @enter="() => updateDashboard(groupId, dashboardId)"
                  />
                </template>
              </div>
            </div>
            <component
              :is="graphComponent(dashboard?.type ?? 'bar')"
              v-if="dashboard?.data"
              :options="dashboard?.options"
              :data="dashboard?.data"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import store from '@/store';
import CONFIG_QUERY from '@/queries/config';
import { XHR, parseJson, formatDateTime } from '@/helpers';
import InputNumber from '@/components/base/InputNumber';
import InputEnum from '@/components/base/InputEnum';

import EditFormString from '@/components/edit-form/fields/EditFormString';
import EditFormDate from '@/components/edit-form/fields/EditFormDate';
import EditFormDateRange from '@/components/edit-form/fields/EditFormDateRange';
import RefAutocomplete from '@/components/edit-form/fields/ref/RefAutocomplete';
import RefAutocomplete2 from '@/components/edit-form/fields/ref/RefAutocomplete2';
import RefCheckboxes from '@/components/edit-form/fields/ref/RefCheckboxes';
import RefCheckboxes2 from '@/components/edit-form/fields/ref/RefCheckboxes2';

import {
  Bar,
  Line as LineChart,
  Doughnut,
  Pie,
  PolarArea,
  Radar,
  Bubble,
  Scatter,
} from 'vue-chartjs';

import {
  Colors,
  Chart as ChartJS,
  Title,
  Tooltip,
  Legend,
  BarElement,
  ArcElement,
  LineElement,
  CategoryScale,
  LinearScale,
  PointElement,
  RadialLinearScale,
} from 'chart.js';
import { updateDatasets, updateChartOptions, dashboardTypes } from './utils';
import { drowLinePlugin, emptyDataPlugin } from './customPlugins';

ChartJS.register(
  Title,
  Tooltip,
  Legend,
  BarElement,
  ArcElement,
  LineElement,
  CategoryScale,
  LinearScale,
  PointElement,
  RadialLinearScale,
  drowLinePlugin,
  emptyDataPlugin,
  Colors,
);

export default {
  name: 'DashboardPage',
  components: {
    InputNumber,
    EditFormString,
    EditFormDate,
    EditFormDateRange,
    InputEnum,
    RefAutocomplete,
    RefAutocomplete2,
    RefCheckboxes,
    RefCheckboxes2,
    Bar,
    Doughnut,
    LineChart,
    Pie,
    PolarArea,
    Radar,
    Bubble,
    Scatter,
  },

  apollo: {
    gqlConfig: {
      ...CONFIG_QUERY,
      variables() {
        return {
          name: `${this.pageConfig.type}_${this.pageConfig.code}`,
        };
      },
      update({ table }) {
        this.dashboardConfig = table.documents?.[0].data?.config ?? [];
        this.loading = false;
        return true;
      },
      error(error) {
        this.emitError(this.$t('app.error.mainConfig'), error.message);
      },
      skip() {
        return this.restUrl;
      },
    },
  },
  data() {
    return {
      filters: [],
      enrichedData: [],
      restUrl: '',
      dashboardConfig: {},
      statisticsGroups: [],
      loading: true,
    };
  },

  computed: {
    pageConfig() {
      return store.state.activeSidebarItem || {};
    },
  },
  watch: {
    dashboardConfig: {
      immediate: true,
      handler(newVal) {
        if (newVal) {
          this.createFilters(newVal);
          this.updateAllData();
        }
      },
    },
    pageConfig() {
      this.statisticsGroups = [];
      this.filters = [];
      if (this.pageConfig?.customprops?.url) {
        this.getRestConfig(this.pageConfig.customprops.url);
      }
    },
  },
  mounted() {
    if (this.pageConfig?.customprops?.url) {
      this.getRestConfig(this.pageConfig.customprops.url);
    }
  },
  methods: {
    formatDateTime,
    async getRestConfig(rawUrl) {
      this.loading = true;
      if (this.restUrl !== rawUrl) {
        this.restUrl = rawUrl;
      }
      const filters2 = {};

      Object.keys(this.filters || {}).forEach((groupKey) => {
        const group = this.filters[groupKey];

        if (group.params) {
          Object.assign(filters2, group.params);
        }

        if (group.children) {
          Object.keys(group.children).forEach((dashboardKey) => {
            const dashboardFiltersObj = group.children[dashboardKey];

            Object.entries(dashboardFiltersObj).forEach(([key, value]) => {
              const config = this.dashboardConfig.params.find((param) => param.label === key);
              if (config) {
                if (config.type === 'date') {
                  filters2[key] = this.formatDateTime(value, 'YYYY-MM-DD');
                } else if (config.type === 'date-range') {
                  filters2[key] = [
                    this.formatDateTime(value[0], 'YYYY-MM-DD'),
                    this.formatDateTime(value[1], 'YYYY-MM-DD'),
                  ];
                } else {
                  filters2[key] = value;
                }
              } else {
                filters2[key] = value;
              }
            });
          });
        }
      });

      const url = this.formatParams(rawUrl, filters2);
      await XHR.get(url, { absoluteUrl: true }).then(
        (response) => {
          // Here goes hardcode for merging tservey graphs with statistic graphs
          // start of hardcode
          if (url.includes('/api/v1/dashboard/survey') && filters2.survey) {
            XHR.get('/api/v1/dashboard/statistics', { absoluteUrl: true }).then(
              (statisticResponse) => {
                this.loading = false;
                const statisticConfig = parseJson(statisticResponse);
                const dashboardConfig = parseJson(response);
                this.dashboardConfig = dashboardConfig;
                this.statisticsGroups = statisticConfig.groups;
              },
            );

            return;
          }
          // end of hardcode

          this.loading = false;
          this.dashboardConfig = parseJson(response);
        },
        () => {
          this.emitError(this.$t(`base.getTable_notification`));
        },
      );
    },
    getFilterComponent(param) {
      const { refDisplay, config, type } = param;

      if (['ref', 'refs'].includes(type) && !config?.urls && !config?.url) {
        if (!this.isInlineTable && refDisplay?.key === 'checkboxes') {
          return 'ref-checkboxes';
        }

        return 'ref-autocomplete';
      }

      if (['ref', 'refs', 'ref2', 'ref-like'].includes(type)) {
        if (!this.isInlineTable && refDisplay?.key === 'checkboxes') {
          return 'ref-checkboxes2';
        }

        return 'ref-autocomplete2';
      }
      return (
        {
          number: 'input-number',
          string: 'edit-form-string',
          enum: 'input-enum',
          date: 'edit-form-date',
          'date-range': 'edit-form-date-range',
        }[type] ?? 'string'
      );
    },
    createFilters(config) {
      this.filters = (config?.groups ?? []).map((group) => ({
        params: this.paramsReducer(group?.params),
        children: group?.dashboards.map((dashboard) => this.paramsReducer(dashboard.params)),
      }));
    },
    paramsReducer(params = []) {
      return params.reduce((acc, value) => {
        acc[value.label] = value?.defaultValue;
        return acc;
      }, {});
    },
    async updateAllData() {
      if (!this.dashboardConfig?.groups) return;

      const updatedDashboardGroups = await Promise.all(
        this.dashboardConfig.groups.map(async (groupConfig, groupIdx) => {
          const updatedGroup = { ...groupConfig };

          updatedGroup.dashboards = await Promise.all(
            groupConfig.dashboards.map((el, elIdx) => this.fetchDashboardData(groupIdx, elIdx, el)),
          );

          updatedGroup.dashboards = updatedGroup.dashboards.map((dashboard) => ({
            ...dashboard,
            data: {
              ...dashboard.data,
              datasets: updateDatasets(dashboard.data.datasets, dashboard.options),
            },
            options: updateChartOptions(dashboard.options, {
              dashboardType: dashboard.type,
              datasetLength: dashboard.data.datasets.length,
            }),
          }));

          return updatedGroup;
        }),
      );

      this.enrichedData = updatedDashboardGroups;
    },
    updateDashboard(groupId, dashboardId) {
      const groupConfig = this.dashboardConfig.groups[groupId];
      const dashboardConfig = groupConfig.dashboards[dashboardId];
      this.fetchDashboardData(groupId, dashboardId, dashboardConfig).then((updatedDashboard) => {
        this.$set(this.enrichedData[groupId].dashboards, dashboardId, updatedDashboard);
      });
    },
    async updateGroupDashboards(groupId) {
      if (this.restUrl) {
        await this.getRestConfig(this.restUrl);
      }
      const groupConfig = this.dashboardConfig.groups[groupId];
      Promise.all(
        groupConfig.dashboards.map((el, elIdx) => this.fetchDashboardData(groupId, elIdx, el)),
      ).then((results) => {
        this.$set(this.enrichedData[groupId], 'dashboards', results);
      });
    },
    fetchDashboardData(groupId, dashboardId, dashboardConfig) {
      return new Promise((resolve) => {
        const groupFilters = this.filters?.[groupId]?.params;
        const dashboardFilters = Object.fromEntries(
          Object.entries(this.filters?.[groupId]?.children?.[dashboardId]).map(([key, value]) => {
            const config = dashboardConfig.params.find((t) => t.label === key);
            if (config.type === 'date') {
              return [key, this.formatDateTime(value, 'YYYY-MM-DD')];
            }
            if (config.type === 'date-range') {
              return [
                key,
                [
                  this.formatDateTime(value[0], 'YYYY-MM-DD'),
                  this.formatDateTime(value[1], 'YYYY-MM-DD'),
                ],
              ];
            }
            return [key, value];
          }),
        );
        if (dashboardConfig?.restUrl) {
          const url = this.formatParams(dashboardConfig.restUrl, {
            ...groupFilters,
            ...dashboardFilters,
          });

          XHR.get(url, { absoluteUrl: true }).then(
            (response) => {
              const data = parseJson(response);
              resolve({ ...dashboardConfig, ...data });
            },
            () => {
              this.emitError(`Failed to load data from ${url}`);
              resolve({});
            },
          );
        } else {
          resolve(dashboardConfig);
        }
      });
    },
    formatParams(url, params) {
      if (!params || !Object.values(params).some((value) => value)) {
        return url;
      }
      return `${url}?${Object.entries(params)
        // eslint-disable-next-line no-unused-vars
        .filter(([_key, value]) => !!value)
        .map(([key, value]) => `${key}=${encodeURIComponent(value)}`)
        .join('&')}`;
    },
    graphComponent(type) {
      return dashboardTypes[type] ?? dashboardTypes.bar;
    },
    graphStyles(graphConfig) {
      return {
        width: `${graphConfig?.width ?? '100'}%`,
      };
    },
    groupStyles(groupConfig) {
      return {
        gridTemplateColumns: `repeat(${groupConfig?.dashboardsOnRow ?? '1'}, minmax(0, 1fr)`,
      };
    },
  },
};
</script>

<style lang="scss">
.dashboard-page {
  padding-bottom: 40px;
  padding-left: 10px;
  padding-right: 10px;
  overflow-x: visible;

  &__title {
    position: relative;
    flex-basis: 100%;
    width: 100%;
    display: flex;
    align-items: center;
    margin-bottom: 15px;
    height: 42px;

    h1 {
      display: inline-block;
      font-size: 28px;
      font-weight: 700;
      vertical-align: sub;
      margin-bottom: 0;
      margin-right: 30px;
    }
  }

  &__list {
    display: flex;
    flex-direction: column;
    gap: 20px;
  }

  &__group {
    &-header {
      display: flex;
      flex-direction: column;
      align-items: flex-start;
      gap: 20px;
      margin-bottom: 40px;
    }

    &-title {
      font-size: 22px;
      text-align: center;
      margin: 0;
    }
    &-body {
      display: grid;
      gap: 40px;
    }
  }

  &__dashboard {
    &-filters {
      display: flex;
      flex-wrap: wrap;
      gap: 10px 20px;
    }
    &-filter {
      display: flex;
      gap: 10px;
      align-items: center;

      .dashboard-page__dashboard-filter-input,
      &-label {
        white-space: nowrap;
        margin-bottom: 0;
      }
      .dashboard-page__dashboard-filter-input {
        min-width: 100px;
      }
    }
  }
}

@media (min-width: $desktopBreakpoint) {
  .dashboard-page {
    @include scrollbars();
    height: 100%;
    overflow-y: scroll;

    &.noscroll {
      overflow-y: hidden;
    }
  }

  .menu--vertical .dashboard-page {
    padding-left: 40px;
    padding-right: 26px;

    &.noscroll {
      padding-right: 34px;
    }
  }

  .menu--horizontal .dashboard-page {
    padding-left: 24px;
    padding-right: 24px;

    &.noscroll {
      padding-right: 32px;
    }
  }
}

@media (max-width: $tabletBreakpoint) {
  .dashboard-page {
    .dashboard-page__group-body {
      grid-template-columns: minmax(0, 1fr) !important;
    }
  }
}
</style>
